/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SecurityModule
 */

import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
} from '@angular/core';

import type {
    Certificate,
} from 'ajs/modules/security/factories/certificates/certificate.item.factory';

import { L10nService } from '@vmw/ngx-vip';
import * as l10n from './certificate-chain-dialog.l10n';
import './certificate-chain-dialog.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Certificate Chain Dialog Component.
 *
 * @author Shahab Hashmi
 */

interface ICertificateChainItem {
    name: string;
    level: number;
    hasChildren: boolean;
    children?: ICertificateChainItem[];
}

@Component({
    selector: 'certificate-chain-dialog',
    templateUrl: './certificate-chain-dialog.component.html',
})
export class CertificateChainDialogComponent implements OnInit {
    /**
     * Certificate Item.
     */
    @Input()
    public certificate: Certificate;

    /**
     * Event to be fired upon user closing the dialog.
     */
    @Output()
    private readonly onClose = new EventEmitter<void>();

    public readonly l10nKeys = l10nKeys;

    /**
     * Certificate chain items for display.
     */
    public chainItems: ICertificateChainItem[] = [];

    /**
     * Root item state.
     */
    public rootItem: ICertificateChainItem = {
        name: '',
        level: 0,
        hasChildren: true,
    };

    constructor(l10nService: L10nService) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        // Set the root item name from the certificate
        this.rootItem.name = this.certificate.config.name;
        this.buildCertificateChain();
    }

    /**
     * Close the dialog.
     */
    public close(): void {
        this.onClose.emit();
    }

    /**
     * Build the certificate chain structure.
     * Generic solution that works with chains of any length (1 to N items).
     */
    private buildCertificateChain(): void {
        this.chainItems = [];

        const seenNames = new Set<string>();

        // Add CA certificates if present
        if (this.certificate.config.ca_certs?.config) {
            // Dynamically determine chain length.
            const totalCerts = this.certificate.config.ca_certs.config.length;

            this.certificate.config.ca_certs.config.forEach((cert: any, index: number) => {
                const certName = cert.config?.name || 'Unknown Certificate';
                const normalizedName = certName.toLowerCase().trim();

                if (!seenNames.has(normalizedName)) {
                    seenNames.add(normalizedName);

                    // Create a visual chain that flows from left to right.
                    // Calculate level for left-to-right progression
                    // First item gets level 1 (minimal indentation, leftmost)
                    // Last item gets level N (maximum indentation, rightmost)
                    const level = index + 1;

                    this.chainItems.push({
                        name: certName,
                        level,
                        hasChildren: index < totalCerts - 1,
                    });
                }
            });
        }

        // Add certificate availability info if different from root and CA certs
        if (this.certificate.certificateAvailability) {
            const availabilityName = this.certificate.certificateAvailability;
            const normalizedName = availabilityName.toLowerCase().trim();
            const rootName = this.certificate.config.name.toLowerCase().trim();

            if (normalizedName !== rootName && !seenNames.has(normalizedName)) {
                this.chainItems.unshift({
                    name: availabilityName,
                    level: 0,
                    hasChildren: this.chainItems.length > 0,
                });
            }
        }
    }

    /**
     * Get the indentation width for a specific level.
     * Balanced indentation system that centers short chains and expands outward
     * for longer chains, keeping content in the main viewing area.
     */
    public getIndentationWidth(level: number): string {
        const baseIndent = 16; // Moderate base indentation for better hierarchy

        // Use balanced progression to center content and expand outward
        let calculatedIndent: number;

        switch (true) {
            case level <= 8: {
                // For first 8 levels, use moderate linear progression
                // Keep short chains centered in the main dialog area.
                calculatedIndent = level * baseIndent;
                break;
            }

            case level <= 15: {
                // For levels 9-15, use reduced progression
                // Maintain center alignment while adding hierarchy
                calculatedIndent = 8 * baseIndent + (level - 8) * (baseIndent * 0.8);
                break;
            }

            case level <= 25: {
                // For levels 16-25, use minimal progression
                // Prevents excessive indentation while maintaining visibility
                const baseCalculation = 8 * baseIndent + 7 * (baseIndent * 0.8);

                calculatedIndent = baseCalculation + (level - 15) * (baseIndent * 0.6);
                break;
            }

            case level <= 40: {
                // For levels 26-40, use very minimal progression
                // Ensure all items remain visible.
                const baseCalculation2 = 8 * baseIndent + 7 * (baseIndent * 0.8) +
                                       10 * (baseIndent * 0.6);

                calculatedIndent = baseCalculation2 + (level - 25) * (baseIndent * 0.4);
                break;
            }

            default: {
                // For levels beyond 40, use fixed minimal indentation
                // Prevent overflow while maintaining hierarchy.
                const baseCalculation3 = 8 * baseIndent + 7 * (baseIndent * 0.8) +
                                       10 * (baseIndent * 0.6) + 15 * (baseIndent * 0.4);

                calculatedIndent = baseCalculation3 + (level - 40) * 3;
                break;
            }
        }

        return `${Math.round(calculatedIndent)}px`;
    }
}
